/**
 * Extension Auth Bridge
 *
 * Broadcasts authentication and entitlement state to the ShowLark browser extension.
 * This allows the extension to know the user's tier and apply appropriate limits.
 *
 * Communication flow:
 * 1. When extension is detected (showlarkExtensionReady event), broadcast current entitlements
 * 2. When auth state changes, broadcast updated entitlements
 * 3. Extension receives via content script → background script
 */

import { authService } from '../../auth/auth-service.js';
import { entitlementService } from '../../entitlements/entitlement-service.js';

// Track if we've set up listeners
let initialized = false;

/**
 * Broadcast current entitlements to extension via postMessage
 */
async function broadcastEntitlements() {
  try {
    const session = authService.getSession();
    const entitlements = await entitlementService.getEntitlements();

    // Only broadcast for non-anonymous authenticated users, or when in free mode
    const isReallyAuthenticated = authService.isAuthenticated() && !authService.isAnonymous();

    const message = {
      type: 'SHOWLARK_ENTITLEMENTS_SYNC',
      authenticated: isReallyAuthenticated,
      token: session?.access_token || null,
      expiresAt: session?.expires_at ? session.expires_at * 1000 : null, // Convert to ms
      userId: session?.user?.id || null,
      tier: entitlements?.tier || 'anonymous',
      tierInfo: entitlements?.tierInfo || {
        displayName: 'Guest',
        badgeLabel: 'GUEST',
        isPaid: false,
      },
      extensionFeatures: {
        enabled: entitlements?.features?.extensionEnabled ?? true,
        maxSongsPerGeneration: entitlements?.features?.extensionMaxSongsPerGeneration ?? 5,
      },
      canUpgrade: entitlements?.canUpgrade ?? true, // Default true; edge function provides accurate value
      freeMode: entitlements?.freeMode || false,
    };

    // Post message to window (extension content script will pick it up)
    window.postMessage(message, window.location.origin);

    console.log('[ExtensionAuthBridge] Broadcasted entitlements:', {
      authenticated: message.authenticated,
      tier: message.tier,
      freeMode: message.freeMode,
      maxSongs: message.extensionFeatures.maxSongsPerGeneration,
    });
  } catch (error) {
    console.error('[ExtensionAuthBridge] Error broadcasting entitlements:', error);
  }
}

/**
 * Initialize the extension auth bridge
 * Sets up listeners for extension detection and auth state changes
 */
function init() {
  if (initialized) return;
  initialized = true;

  // When extension is detected, broadcast current entitlements
  document.addEventListener('showlarkExtensionReady', async (event) => {
    console.log('[ExtensionAuthBridge] Extension detected, version:', event.detail?.version);
    // Small delay to ensure extension is fully ready
    setTimeout(broadcastEntitlements, 100);
  });

  // When auth state changes, broadcast updated entitlements
  authService.onAuthStateChange(async (event, session) => {
    console.log('[ExtensionAuthBridge] Auth state changed:', event);
    // Small delay to allow entitlements to refresh
    setTimeout(broadcastEntitlements, 200);
  });

  // Also listen for entitlement changes (tier upgrades, etc.)
  entitlementService.onEntitlementsChange(async (newEntitlements) => {
    console.log('[ExtensionAuthBridge] Entitlements changed, tier:', newEntitlements?.tier);
    broadcastEntitlements();
  });

  console.log('[ExtensionAuthBridge] Initialized');
}

// Auto-initialize when module is imported
init();

export { broadcastEntitlements, init };
